import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { trackTimeEntryCreated, trackFormSubmission } from '@/lib/posthog-events';
import { localDateString } from '@/lib/time-entry-time';
import { createTimeEntry } from './api';

/**
 * THE time tracker -- application-level, not owned by the Time Tracking page.
 * It keeps running while the user navigates anywhere (and whatever the Global
 * Project Selector says): the session is pinned to the project it started on,
 * is kept in localStorage (so a browser refresh does not lose it), and the
 * elapsed time is always computed from its start instant (never counted tick
 * by tick), so it is right after a refresh, a background tab or a sleeping laptop.
 * It only ends when the user stops it, which saves the entry through the API.
 */
export interface TrackerSession {
  projectId: string;
  projectName: string;
  /** ISO instant the tracker started. */
  startTime: string;
}

interface TimeTrackerContextType {
  session: TrackerSession | null;
  isRunning: boolean;
  elapsedSeconds: number;
  isSaving: boolean;
  /** Incremented every time an entry is saved by the tracker, so lists can reload. */
  savedVersion: number;
  start: (projectId: string, projectName: string) => void;
  /** Stops and saves. Throws if the save fails -- the session is kept, nothing is lost. */
  stop: () => Promise<void>;
}

const noop = async () => {};
const TimeTrackerContext = createContext<TimeTrackerContextType>({
  session: null, isRunning: false, elapsedSeconds: 0, isSaving: false, savedVersion: 0, start: () => {}, stop: noop,
});

export const useTimeTracker = () => useContext(TimeTrackerContext);

const STORAGE_KEY = 'krewup.timeTracker';
interface Stored { userId: string; companyId: string; session: TrackerSession }

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const s = parsed?.session;
    if (parsed && typeof parsed.userId === 'string' && typeof parsed.companyId === 'string' && s
      && typeof s.projectId === 'string' && typeof s.projectName === 'string' && !Number.isNaN(new Date(s.startTime).getTime())) {
      return parsed as Stored;
    }
  } catch {
    // unreadable: no session
  }
  return null;
}

function writeStored(value: Stored | null) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable: the tracker still runs, it just won't survive a refresh
  }
}

export function TimeTrackerProvider({ children }: { children: React.ReactNode }) {
  const { user, companyId, isLoading } = useUser();
  const userId = user?.id as string | undefined;
  const [session, setSession] = useState<TrackerSession | null>(() => readStored()?.session ?? null);
  const [now, setNow] = useState(() => Date.now());
  const [isSaving, setIsSaving] = useState(false);
  const [savedVersion, setSavedVersion] = useState(0);
  const checkedIdentity = useRef<string | null>(null);

  // A restored session belongs to one user in one company; anyone else never inherits it.
  useEffect(() => {
    if (isLoading || !userId || !companyId) return;
    const identity = `${userId}:${companyId}`;
    if (checkedIdentity.current === identity) return;
    checkedIdentity.current = identity;
    const stored = readStored();
    if (stored && (stored.userId !== userId || stored.companyId !== companyId)) {
      setSession(null);
      writeStored(null);
    }
  }, [isLoading, userId, companyId]);

  useEffect(() => {
    if (!session) return undefined;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [session]);

  const start = useCallback((projectId: string, projectName: string) => {
    if (!userId || !companyId) return;
    setSession((current) => {
      if (current) return current; // already tracking: the project stays pinned
      const next: TrackerSession = { projectId, projectName, startTime: new Date().toISOString() };
      writeStored({ userId, companyId, session: next });
      return next;
    });
  }, [userId, companyId]);

  const stop = useCallback(async () => {
    if (!session || isSaving) return;
    const end = new Date();
    const startMs = new Date(session.startTime).getTime();
    if (end.getTime() - startMs < 1000) { // nothing worth saving
      setSession(null);
      writeStored(null);
      return;
    }
    setIsSaving(true);
    try {
      const created = await createTimeEntry({
        projectId: session.projectId,
        date: localDateString(new Date(session.startTime)),
        startISO: session.startTime,
        endISO: end.toISOString(),
      });
      setSession(null);
      writeStored(null);
      setSavedVersion((v) => v + 1);
      try {
        trackTimeEntryCreated({
          time_entry_id: created.id, project_id: session.projectId, duration_hours: (end.getTime() - startMs) / 3600000,
          date: localDateString(new Date(session.startTime)), start_time: session.startTime, end_time: end.toISOString(),
          task_type: 'timer_session', billable: true, company_id: companyId, created_by: userId,
        });
        trackFormSubmission({ form_type: 'timer_time_entry_creation', success: true });
      } catch (analyticsError) {
        console.error('Analytics failed:', analyticsError);
      }
    } catch (err) {
      trackFormSubmission({ form_type: 'timer_time_entry_creation', success: false, error_message: err instanceof Error ? err.message : 'Unknown error' });
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [session, isSaving, companyId, userId]);

  const elapsedSeconds = session ? Math.max(0, Math.floor((now - new Date(session.startTime).getTime()) / 1000)) : 0;

  return (
    <TimeTrackerContext.Provider value={{ session, isRunning: !!session, elapsedSeconds, isSaving, savedVersion, start, stop }}>
      {children}
    </TimeTrackerContext.Provider>
  );
}
